import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Label } from '../../components/ui/Label';
import {
  Code, Plus, Search, Filter, Edit3, Trash2, Eye, Save, RefreshCw,
  CheckCircle, AlertTriangle, X, Target, Activity, Users, MessageSquare,
  Calendar, Clock, Zap, Star, Heart, Coffee, Pizza, Cake, Music,
  Camera, Video, File, Share, Bookmark, Flag, Monitor, Cpu, HardDrive,
  Network, Wifi, Signal, Battery, Volume, Volume2, VolumeX, Play,
  Pause, Stop, SkipBack, SkipForward, RotateCcw, RotateCw, Maximize,
  Minimize, Maximize2, Minimize2, Square, Circle, Triangle, Hexagon,
  Octagon, Pentagon, Diamond, Star as StarIcon, PieChart as PieChartIcon,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, MoreHorizontal, MoreVertical, Copy,
  ExternalLink, Lock, Unlock, Shield, Database, Server, Globe,
  AlertCircle, CheckCircle2, XCircle, Settings, Wrench, BarChart3,
  TrendingUp, TrendingDown, Key, Shield as ShieldIcon, Database as DatabaseIcon
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const APILimits = () => {
  const [apiLimits, setApiLimits] = useState([]);
  const [usageStats, setUsageStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('limits'); // 'limits', 'usage', 'analytics'

  const [newLimit, setNewLimit] = useState({
    name: '',
    description: '',
    endpoint: '',
    method: 'GET',
    limit: 1000,
    period: 'hour',
    status: 'active',
    priority: 'medium',
    throttling: {
      enabled: true,
      burstLimit: 100,
      rateLimit: 50
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [limitsResponse, usageResponse] = await Promise.all([
          MockDataService.getAPILimits(),
          MockDataService.getAPIUsageStats()
        ]);
        
        if (limitsResponse.success) {
          setApiLimits(limitsResponse.data);
        }
        if (usageResponse.success) {
          setUsageStats(usageResponse.data);
        }
      } catch (err) {
        setError('Error loading API limits data');
        console.error('API limits error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateLimit = async () => {
    try {
      const response = await MockDataService.createAPILimit(newLimit);
      if (response.success) {
        setApiLimits([...apiLimits, response.data]);
        setShowForm(false);
        setNewLimit({
          name: '',
          description: '',
          endpoint: '',
          method: 'GET',
          limit: 1000,
          period: 'hour',
          status: 'active',
          priority: 'medium',
          throttling: {
            enabled: true,
            burstLimit: 100,
            rateLimit: 50
          }
        });
      }
    } catch (err) {
      setError('Failed to create API limit');
    }
  };

  const handleUpdateLimit = async (limitId, updates) => {
    try {
      const response = await MockDataService.updateAPILimit(limitId, updates);
      if (response.success) {
        setApiLimits(apiLimits.map(limit => 
          limit.id === limitId ? { ...limit, ...updates } : limit
        ));
      }
    } catch (err) {
      setError('Failed to update API limit');
    }
  };

  const handleDeleteLimit = async (limitId) => {
    if (window.confirm('Are you sure you want to delete this API limit?')) {
      try {
        const response = await MockDataService.deleteAPILimit(limitId);
        if (response.success) {
          setApiLimits(apiLimits.filter(limit => limit.id !== limitId));
        }
      } catch (err) {
        setError('Failed to delete API limit');
      }
    }
  };

  const filteredLimits = apiLimits.filter(limit => {
    const matchesSearch = limit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         limit.endpoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         limit.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || limit.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'PATCH': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading API limits...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <span className="ml-2 text-red-500">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">API Limits Management</h1>
          <p className="text-muted-foreground mt-2">
            Manage API rate limits, throttling, and usage monitoring for developer integrations
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-primary hover:bg-primary/90">
          <Plus className="h-4 w-4 mr-2" />
          Add API Limit
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'limits', label: 'API Limits', icon: Shield },
            { id: 'usage', label: 'Usage Stats', icon: BarChart3 },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* API Limits Tab */}
      {activeTab === 'limits' && (
        <div className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search API limits..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* API Limits List */}
          <div className="grid gap-4">
            {filteredLimits.map((limit) => (
              <Card key={limit.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{limit.name}</h3>
                        <Badge className={getStatusColor(limit.status)}>
                          {limit.status.toUpperCase()}
                        </Badge>
                        <Badge className={getPriorityColor(limit.priority)}>
                          {limit.priority.toUpperCase()}
                        </Badge>
                        <Badge className={getMethodColor(limit.method)}>
                          {limit.method}
                        </Badge>
                      </div>
                      
                      <p className="text-muted-foreground mb-3">{limit.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Code className="h-4 w-4" />
                          {limit.endpoint}
                        </div>
                        <div className="flex items-center gap-1">
                          <Target className="h-4 w-4" />
                          {limit.limit}/{limit.period}
                        </div>
                        <div className="flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          {limit.currentUsage || 0} used
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Updated: {new Date(limit.updatedAt).toLocaleDateString()}
                        </div>
                      </div>

                      {limit.throttling.enabled && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <div className="text-sm font-medium text-blue-800 mb-1">Throttling Settings</div>
                          <div className="text-sm text-blue-700">
                            Burst Limit: {limit.throttling.burstLimit} | Rate Limit: {limit.throttling.rateLimit}/min
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedLimit(limit)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedLimit(limit);
                          setShowForm(true);
                        }}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteLimit(limit.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Usage Stats Tab */}
      {activeTab === 'usage' && (
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                API Usage Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {usageStats.totalRequests || '12,456'}
                  </div>
                  <div className="text-sm text-blue-600">Total Requests (24h)</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {usageStats.successRate || '98.5'}%
                  </div>
                  <div className="text-sm text-green-600">Success Rate</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {usageStats.rateLimitHits || '23'}
                  </div>
                  <div className="text-sm text-yellow-600">Rate Limit Hits</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {usageStats.errors || '45'}
                  </div>
                  <div className="text-sm text-red-600">Errors</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Endpoints</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { endpoint: '/api/tickets', requests: 3456, method: 'GET' },
                        { endpoint: '/api/users', requests: 2341, method: 'GET' },
                        { endpoint: '/api/tickets', requests: 1890, method: 'POST' },
                        { endpoint: '/api/auth/login', requests: 1234, method: 'POST' },
                        { endpoint: '/api/knowledge', requests: 987, method: 'GET' }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{item.endpoint}</div>
                            <Badge className={getMethodColor(item.method)}>
                              {item.method}
                            </Badge>
                          </div>
                          <Badge variant="secondary">{item.requests}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Rate Limit Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {apiLimits.slice(0, 5).map((limit) => (
                        <div key={limit.id} className="border rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{limit.name}</span>
                            <Badge className={getStatusColor(limit.status)}>
                              {limit.status}
                            </Badge>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                limit.currentUsage / limit.limit > 0.9 ? 'bg-red-500' :
                                limit.currentUsage / limit.limit > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min((limit.currentUsage / limit.limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {limit.currentUsage || 0} / {limit.limit} requests
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                API Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {usageStats.avgResponseTime || '245'}ms
                  </div>
                  <div className="text-sm text-purple-600">Avg Response Time</div>
                </div>
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <div className="text-2xl font-bold text-indigo-600">
                    {usageStats.activeKeys || '156'}
                  </div>
                  <div className="text-sm text-indigo-600">Active API Keys</div>
                </div>
                <div className="text-center p-4 bg-teal-50 rounded-lg">
                  <div className="text-2xl font-bold text-teal-600">
                    {usageStats.dataTransfer || '2.4'}GB
                  </div>
                  <div className="text-sm text-teal-600">Data Transfer (24h)</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>API Key Usage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { key: 'dev-key-001', usage: 89, status: 'active' },
                        { key: 'dev-key-002', usage: 67, status: 'active' },
                        { key: 'dev-key-003', usage: 45, status: 'warning' },
                        { key: 'dev-key-004', usage: 23, status: 'inactive' },
                        { key: 'dev-key-005', usage: 12, status: 'active' }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{item.key}</div>
                            <Badge className={getStatusColor(item.status)}>
                              {item.status}
                            </Badge>
                          </div>
                          <Badge variant="secondary">{item.usage}%</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Error Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { error: '429 Too Many Requests', count: 15, percentage: 33 },
                        { error: '401 Unauthorized', count: 12, percentage: 27 },
                        { error: '400 Bad Request', count: 8, percentage: 18 },
                        { error: '500 Internal Server Error', count: 6, percentage: 13 },
                        { error: '403 Forbidden', count: 4, percentage: 9 }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="font-medium">{item.error}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{item.count}</Badge>
                            <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <CardContent className="bg-background p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {selectedLimit ? 'Edit API Limit' : 'Add New API Limit'}
              </h2>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setSelectedLimit(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Limit Name</Label>
                <Input
                  id="name"
                  value={selectedLimit?.name || newLimit.name}
                  onChange={(e) => {
                    if (selectedLimit) {
                      setSelectedLimit({ ...selectedLimit, name: e.target.value });
                    } else {
                      setNewLimit({ ...newLimit, name: e.target.value });
                    }
                  }}
                  placeholder="Enter limit name"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={selectedLimit?.description || newLimit.description}
                  onChange={(e) => {
                    if (selectedLimit) {
                      setSelectedLimit({ ...selectedLimit, description: e.target.value });
                    } else {
                      setNewLimit({ ...newLimit, description: e.target.value });
                    }
                  }}
                  placeholder="Describe the API limit"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="endpoint">Endpoint</Label>
                  <Input
                    id="endpoint"
                    value={selectedLimit?.endpoint || newLimit.endpoint}
                    onChange={(e) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, endpoint: e.target.value });
                      } else {
                        setNewLimit({ ...newLimit, endpoint: e.target.value });
                      }
                    }}
                    placeholder="/api/endpoint"
                  />
                </div>

                <div>
                  <Label htmlFor="method">HTTP Method</Label>
                  <Select
                    value={selectedLimit?.method || newLimit.method}
                    onValueChange={(value) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, method: value });
                      } else {
                        setNewLimit({ ...newLimit, method: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="limit">Rate Limit</Label>
                  <Input
                    id="limit"
                    type="number"
                    value={selectedLimit?.limit || newLimit.limit}
                    onChange={(e) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, limit: parseInt(e.target.value) });
                      } else {
                        setNewLimit({ ...newLimit, limit: parseInt(e.target.value) });
                      }
                    }}
                    placeholder="1000"
                  />
                </div>

                <div>
                  <Label htmlFor="period">Period</Label>
                  <Select
                    value={selectedLimit?.period || newLimit.period}
                    onValueChange={(value) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, period: value });
                      } else {
                        setNewLimit({ ...newLimit, period: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minute">Per Minute</SelectItem>
                      <SelectItem value="hour">Per Hour</SelectItem>
                      <SelectItem value="day">Per Day</SelectItem>
                      <SelectItem value="month">Per Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={selectedLimit?.status || newLimit.status}
                    onValueChange={(value) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, status: value });
                      } else {
                        setNewLimit({ ...newLimit, status: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={selectedLimit?.priority || newLimit.priority}
                    onValueChange={(value) => {
                      if (selectedLimit) {
                        setSelectedLimit({ ...selectedLimit, priority: value });
                      } else {
                        setNewLimit({ ...newLimit, priority: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedLimit(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateLimit}>
                  <Save className="h-4 w-4 mr-2" />
                  {selectedLimit ? 'Update Limit' : 'Create Limit'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default APILimits;
